/**
 * Validates if a file matches the accept criteria.
 *
 * Supports the same format as the HTML input accept attribute:
 * - MIME type wildcards (e.g., 'image/*', 'audio/*')
 * - Exact MIME types (e.g., 'application/pdf', 'image/png')
 * - File extensions with dot (e.g., '.pdf', '.jpg')
 * - File extensions without dot (e.g., 'pdf', 'jpg')
 * - Mixed formats (e.g., 'image/*,.pdf,application/json')
 *
 * @param file - The file to validate
 * @param accept - Accept string in the same format as HTML input accept attribute
 * @returns true if file is valid, false otherwise
 *
 * @example
 * isFileTypeValid(file, 'image/*')              // accepts all images
 * isFileTypeValid(file, '.pdf,.doc')            // accepts PDF and DOC files
 * isFileTypeValid(file, 'image/*,.pdf')         // accepts all images and PDFs
 * isFileTypeValid(file, 'application/pdf')      // accepts only PDF MIME type
 */
export function isFileTypeValid(file: File, accept: string): boolean {
  if (!accept || accept.trim() === '') {
    return true; // No restrictions
  }

  const acceptParts = accept.split(',').map((part) => part.trim().toLowerCase());
  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();

  return acceptParts.some((acceptPart) => {
    // Handle MIME type wildcards (e.g., 'image/*', 'audio/*')
    if (acceptPart.includes('/*')) {
      const mimeCategory = acceptPart.split('/')[0];
      return fileType.startsWith(mimeCategory + '/');
    }

    // Handle exact MIME types (e.g., 'application/pdf', 'image/png')
    if (acceptPart.includes('/')) {
      return fileType === acceptPart;
    }

    // Handle file extensions (e.g., '.pdf', '.jpg')
    if (acceptPart.startsWith('.')) {
      return fileName.endsWith(acceptPart);
    }

    // Handle extensions without dot (e.g., 'pdf', 'jpg')
    return fileName.endsWith('.' + acceptPart);
  });
}

/**
 * Convert a file size in bytes into a human-readable string with appropriate units.
 *
 * Uses binary units (1 KB = 1024 bytes) and supports Bytes, KB, MB, GB, TB.
 * Returns an empty string for null, undefined, or NaN values.
 *
 * @param bytes - The file size in bytes
 * @param decimals - Number of decimal places to display (default: 2)
 * @returns Formatted file size string with units (e.g., "1.5 MB", "0 Bytes")
 *
 * @example
 * formatFileSize(0)           // "0 Bytes"
 * formatFileSize(1024)        // "1 KB"
 * formatFileSize(1536, 2)     // "1.5 KB"
 * formatFileSize(1048576)     // "1 MB"
 * formatFileSize(null)        // ""
 */
export function formatFileSize(bytes: number | null | undefined, decimals = 2): string {
  if (bytes == null || isNaN(bytes)) {
    return '';
  }

  if (bytes === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];

  const magnitude = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));
  const i = Math.min(Math.max(magnitude, 0), sizes.length - 1);

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}
